/**
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: valeriaplutino
 * Created: 7/12/21
 */

import * as React from 'react';
import { Button, ButtonProps } from '@chakra-ui/react';
import { Location, matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import { NavigationRoute } from '@/routes/navigation';
import omit from 'lodash/omit';

function checkIfActive(location: Location, when: string[]): boolean {
    return !!matchRoutes(
        when.map((path) => ({ path })),
        location,
    );
}

type NavButtonProps = ButtonProps &
    NavigationRoute & {
        isFullWidth?: boolean;
        justifyContent?: React.CSSProperties['justifyContent'];
        afterClick?: () => void;
    };

export const NavigationButton = ({ path, getPath, afterClick, ...props }: NavButtonProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleClick = () => {
        if (checkIfActive(location, props.when)) return;

        if (path) navigate(path);
        if (getPath) navigate(getPath(location));
        if (afterClick) afterClick();
    };
    const isActive = checkIfActive(location, props.when);

    return (
        <Button
            bg={isActive ? 'blue.500' : 'white'}
            justifyContent={props.justifyContent}
            rounded={0}
            color={isActive ? 'white' : 'black'}
            _hover={{
                bg: 'blue.550',
                color: 'white',
            }}
            _focus={{
                outline: '3px solid #000000',
                outlineOffset: 2,
            }}
            _focusVisible={{
                outline: '3px solid #000000',
                outlineOffset: 2,
            }}
            _active={{
                bg: 'blue.550',
                color: 'white',
            }}
            border={isActive ? 'none' : '1px'}
            borderStyle="solid"
            borderColor="gray.200"
            onClick={() => {
                handleClick();
            }}
            {...omit(props, 'title')}
            role="link"
        >
            {props.title}
        </Button>
    );
};
